/**
 * ============================================================================
 * CREATE LIST MODAL v3.0 - KALENDER-INTEGRATION
 * ============================================================================
 *
 * ✅ v3.0: KALENDER-INTEGRATION
 *    - Checkbox "Zum Kalender hinzufügen"
 *    - Zeitfeld für Kalender-Eintrag
 *    - Einkaufsliste wird mit Kalender verknüpft (sourceShoppingListId)
 *
 * ✅ v2.3: ProductPicker Integration für einfache Artikelauswahl
 * ✅ v2.0: Dynamische Haushaltsmitglieder aus HouseholdContext mit useMemo
 *
 * @version 3.0.0
 */

import React, { useState, useMemo } from 'react';
import { useHousehold } from '../../context/HouseholdContext';
import ProductPicker from './ProductPicker';
import './Shopping.css';

const CreateListModal = ({ onClose, onCreate }) => {
  
  // ✅ HouseholdContext für dynamische Mitglieder
  const { getMemberOptions, members } = useHousehold();
  
  // ✅ useMemo damit FAMILY_MEMBERS neu berechnet wird wenn members sich ändert
  const FAMILY_MEMBERS = useMemo(() => {
    return getMemberOptions(true);
  }, [members, getMemberOptions]);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    assignedTo: 'alle',
    priority: 'normal',
    dueDate: ''
  });

  // ✅ v3.0: Kalender-Integration State
  const [addToCalendar, setAddToCalendar] = useState(false);
  const [calendarTime, setCalendarTime] = useState('10:00');

  const [items, setItems] = useState([]);
  const [newItem, setNewItem] = useState({
    name: '',
    quantity: 1,
    unit: 'Stück',
    category: 'Sonstiges'
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showProductPicker, setShowProductPicker] = useState(false);

  const categories = [
    'Obst & Gemüse',
    'Milchprodukte',
    'Brot & Backwaren',
    'Fleisch & Fisch',
    'Getränke',
    'Snacks',
    'Tiefkühl',
    'Konserven',
    'Haushalt',
    'Sonstiges'
  ];

  const units = [
    'Stück',
    'kg',
    'g',
    'L',
    'ml',
    'Packung',
    'Dose',
    'Flasche',
    'Bund'
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleItemChange = (e) => {
    const { name, value } = e.target;
    setNewItem(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddItem = () => {
    if (!newItem.name.trim()) {
      alert('Bitte gib einen Artikel-Namen ein!');
      return;
    }

    const item = {
      id: 'item-' + Date.now(),
      name: newItem.name.trim(),
      quantity: parseFloat(newItem.quantity) || 1,
      unit: newItem.unit,
      category: newItem.category,
      checked: false
    };

    setItems(prev => [...prev, item]);
    
    setNewItem({
      name: '',
      quantity: 1,
      unit: 'Stück',
      category: 'Sonstiges'
    });
  };

  const handleRemoveItem = (itemId) => {
    setItems(prev => prev.filter(item => item.id !== itemId));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && newItem.name.trim()) {
      e.preventDefault();
      handleAddItem();
    }
  };

  // Handler für ProductPicker - fügt ausgewählte Artikel zur Liste hinzu
  const handleAddItemsFromPicker = (newItems) => {
    setItems(prev => [...prev, ...newItems]);
    setShowProductPicker(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      alert('Bitte gib einen Listen-Namen ein!');
      return;
    }

    // ✅ v3.0: Warnung wenn Kalender gewünscht aber kein Datum
    if (addToCalendar && !formData.dueDate) {
      alert('Bitte wähle ein Fälligkeitsdatum, um einen Kalendereintrag zu erstellen.');
      return;
    }

    setIsSubmitting(true);

    try {
      const dataWithItems = {
        ...formData,
        items: items,
        // ✅ v3.0: Kalender-Daten hinzufügen
        addToCalendar: addToCalendar && formData.dueDate,
        calendarTime: calendarTime
      };

      await onCreate(dataWithItems);
      onClose();
    } catch (error) {
      console.error('Fehler beim Erstellen:', error);
      alert('Fehler beim Erstellen der Liste');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
        
        <div style={{
          padding: '6px 12px',
          background: '#f3f4f6',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span style={{
            fontSize: '0.85rem',
            fontWeight: '600',
            color: '#6b7280',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>🛒 Neue Einkaufsliste</span>
          <button
            onClick={onClose}
            type="button"
            style={{
              background: 'transparent',
              border: 'none',
              fontSize: '1rem',
              color: '#9ca3af',
              cursor: 'pointer',
              padding: '2px 6px',
              lineHeight: 1
            }}
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body" style={{ padding: '12px 16px' }}>

            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'flex', alignItems: 'center', fontSize: '0.8rem', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>
                <span style={{ marginRight: '6px' }}>📝</span>
                Listen-Name *
              </label>
              <input
                type="text"
                name="name"
                placeholder="z.B. Wocheneinkauf, Geburtstagsparty"
                value={formData.name}
                onChange={handleChange}
                required
                autoFocus
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  fontSize: '0.85rem',
                  borderRadius: '8px',
                  border: '1.5px solid #B8D4DC',
                  background: 'white',
                  outline: 'none'
                }}
              />
            </div>

            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'flex', alignItems: 'center', fontSize: '0.8rem', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>
                <span style={{ marginRight: '6px' }}>💬</span>
                Beschreibung (optional)
              </label>
              <textarea
                name="description"
                placeholder="Optionale Beschreibung..."
                value={formData.description}
                onChange={handleChange}
                rows="2"
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  fontSize: '0.85rem',
                  borderRadius: '8px',
                  minHeight: '50px',
                  border: '1.5px solid #B8D4DC',
                  background: 'white',
                  outline: 'none',
                  resize: 'vertical',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>

              {/* ✅ DYNAMISCHES PERSON-DROPDOWN */}
              <div>
                <label style={{ display: 'flex', alignItems: 'center', fontSize: '0.8rem', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>
                  <span style={{ marginRight: '6px' }}>👤</span>
                  Person
                </label>
                <select
                  name="assignedTo"
                  value={formData.assignedTo}
                  onChange={handleChange}
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    fontSize: '0.85rem',
                    borderRadius: '8px',
                    border: '1.5px solid #B8D4DC',
                    background: 'white',
                    outline: 'none',
                    cursor: 'pointer'
                  }}
                >
                  {FAMILY_MEMBERS.map(member => (
                    <option key={member.id} value={member.id}>
                      {member.avatar} {member.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: 'flex', alignItems: 'center', fontSize: '0.8rem', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>
                  <span style={{ marginRight: '6px' }}>⚡</span>
                  Priorität
                </label>
                <select
                  name="priority"
                  value={formData.priority}
                  onChange={handleChange}
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    fontSize: '0.85rem',
                    borderRadius: '8px',
                    border: '1.5px solid #B8D4DC',
                    background: 'white',
                    outline: 'none',
                    cursor: 'pointer'
                  }}
                >
                  <option value="low">🟢 Niedrig</option>
                  <option value="normal">🟡 Normal</option>
                  <option value="high">🔴 Hoch</option>
                </select>
              </div>

            </div>

            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'flex', alignItems: 'center', fontSize: '0.8rem', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>
                <span style={{ marginRight: '6px' }}>📅</span>
                Fälligkeitsdatum (optional)
              </label>
              <input
                type="date"
                name="dueDate"
                value={formData.dueDate}
                onChange={handleChange}
                min={new Date().toISOString().split('T')[0]}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  fontSize: '0.85rem',
                  borderRadius: '8px',
                  border: '1.5px solid #B8D4DC',
                  background: 'white',
                  outline: 'none'
                }}
              />
            </div>

            {/* ✅ v3.0: Kalender-Integration */}
            <div style={{
              marginBottom: '10px',
              padding: '10px 12px',
              background: addToCalendar ? '#E8F4F8' : '#F9FAFB',
              borderRadius: '8px',
              border: `1.5px solid ${addToCalendar ? '#4A90A4' : '#E5E7EB'}`,
              transition: 'all 0.2s ease'
            }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer',
                fontSize: '0.85rem',
                fontWeight: '600',
                color: addToCalendar ? '#4A90A4' : '#6B7280'
              }}>
                <input
                  type="checkbox"
                  checked={addToCalendar}
                  onChange={(e) => setAddToCalendar(e.target.checked)}
                  style={{
                    width: '18px',
                    height: '18px',
                    accentColor: '#4A90A4',
                    cursor: 'pointer'
                  }}
                />
                <span>📅</span>
                Zum Kalender hinzufügen
              </label>

              {/* Zeitfeld - nur sichtbar wenn Checkbox aktiv */}
              {addToCalendar && (
                <div style={{
                  marginTop: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <label style={{
                    fontSize: '0.8rem',
                    color: '#6B7280',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    <span>🕐</span> Uhrzeit:
                  </label>
                  <input
                    type="time"
                    value={calendarTime}
                    onChange={(e) => setCalendarTime(e.target.value)}
                    style={{
                      padding: '6px 10px',
                      fontSize: '0.85rem',
                      borderRadius: '6px',
                      border: '1.5px solid #B8D4DC',
                      background: 'white',
                      outline: 'none'
                    }}
                  />
                  <span style={{
                    fontSize: '0.75rem',
                    color: '#9CA3AF',
                    fontStyle: 'italic'
                  }}>
                    {formData.dueDate ? `am ${new Date(formData.dueDate).toLocaleDateString('de-DE')}` : '(Datum wählen)'}
                  </span>
                </div>
              )}

              {/* Info wenn kein Datum gewählt */}
              {addToCalendar && !formData.dueDate && (
                <div style={{
                  marginTop: '8px',
                  padding: '6px 10px',
                  background: '#FEF3C7',
                  borderRadius: '6px',
                  fontSize: '0.75rem',
                  color: '#92400E',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <span>⚠️</span>
                  Bitte wähle ein Fälligkeitsdatum, um einen Kalendereintrag zu erstellen.
                </div>
              )}
            </div>

            <div style={{ margin: '12px 0 8px 0', padding: '6px 0', borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb' }}>
              <span style={{ fontSize: '0.8rem', fontWeight: '600', color: '#4b5563' }}>🛍️ Artikel hinzufügen</span>
            </div>

            {/* Buttons für Artikelauswahl */}
            <div style={{
              display: 'flex',
              gap: '10px',
              marginBottom: '12px'
            }}>
              <button
                type="button"
                onClick={() => setShowProductPicker(true)}
                style={{
                  flex: 1,
                  padding: '10px 16px',
                  background: 'linear-gradient(135deg, #4A90A4, #6BA893)',
                  border: 'none',
                  borderRadius: '10px',
                  color: 'white',
                  fontWeight: '600',
                  fontSize: '0.85rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  boxShadow: '0 2px 8px rgba(74, 144, 164, 0.25)',
                  transition: 'transform 0.2s, box-shadow 0.2s'
                }}
              >
                <span>📦</span> Artikel auswählen
              </button>
              <button
                type="button"
                onClick={() => document.querySelector('.item-name input')?.focus()}
                style={{
                  padding: '10px 16px',
                  background: '#F3F4F6',
                  border: '1px solid #E5E7EB',
                  borderRadius: '10px',
                  color: '#6B7280',
                  fontWeight: '600',
                  fontSize: '0.85rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px'
                }}
              >
                <span>✏️</span> Manuell
              </button>
            </div>

            <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '10px' }}>
              <input
                type="text"
                name="name"
                placeholder="z.B. Milch..."
                value={newItem.name}
                onChange={handleItemChange}
                onKeyPress={handleKeyPress}
                style={{
                  flex: 3,
                  padding: '6px 8px',
                  fontSize: '0.8rem',
                  borderRadius: '6px',
                  border: '1.5px solid #B8D4DC',
                  background: 'white',
                  outline: 'none',
                  minWidth: '120px'
                }}
              />
              <input
                type="number"
                name="quantity"
                placeholder="1"
                value={newItem.quantity}
                onChange={handleItemChange}
                min="0.1"
                step="0.1"
                style={{
                  width: '45px',
                  padding: '6px 4px',
                  fontSize: '0.8rem',
                  borderRadius: '6px',
                  border: '1.5px solid #B8D4DC',
                  background: 'white',
                  outline: 'none',
                  textAlign: 'center'
                }}
              />
              <select
                name="unit"
                value={newItem.unit}
                onChange={handleItemChange}
                style={{
                  padding: '6px 2px',
                  fontSize: '0.7rem',
                  borderRadius: '6px',
                  border: '1.5px solid #B8D4DC',
                  background: 'white',
                  outline: 'none',
                  cursor: 'pointer',
                  width: '55px'
                }}
              >
                {units.map(unit => (
                  <option key={unit} value={unit}>{unit}</option>
                ))}
              </select>
              <select
                name="category"
                value={newItem.category}
                onChange={handleItemChange}
                style={{
                  padding: '6px 2px',
                  fontSize: '0.7rem',
                  borderRadius: '6px',
                  border: '1.5px solid #B8D4DC',
                  background: 'white',
                  outline: 'none',
                  cursor: 'pointer',
                  width: '70px'
                }}
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={handleAddItem}
                disabled={!newItem.name.trim()}
                style={{
                  padding: '6px 8px',
                  fontSize: '0.85rem',
                  fontWeight: '700',
                  borderRadius: '6px',
                  border: 'none',
                  background: newItem.name.trim() ? '#4A90A4' : '#d1d5db',
                  color: 'white',
                  cursor: newItem.name.trim() ? 'pointer' : 'not-allowed'
                }}
              >
                +
              </button>
            </div>

            {items.length > 0 && (
              <div className="items-preview">
                <div className="items-preview-header">
                  <span className="items-count">
                    📦 {items.length} {items.length === 1 ? 'Artikel' : 'Artikel'}
                  </span>
                </div>
                
                <div className="items-list">
                  {items.map((item) => (
                    <div key={item.id} className="item-preview-card">
                      <div className="item-info">
                        <span className="item-name">{item.name}</span>
                        <span className="item-details">
                          {item.quantity} {item.unit} • {item.category}
                        </span>
                      </div>
                      <button
                        type="button"
                        className="btn-remove-item"
                        onClick={() => handleRemoveItem(item.id)}
                        title="Entfernen"
                      >
                        🗑️
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="info-box">
              <span className="info-icon">💡</span>
              <span className="info-text">
                Du kannst Artikel jetzt hinzufügen oder später bearbeiten!
              </span>
            </div>

          </div>

          <div style={{
            padding: '6px 12px',
            background: '#f3f4f6',
            borderTop: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '8px'
          }}>
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              style={{
                padding: '5px 12px',
                background: 'white',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '0.8rem',
                fontWeight: '500',
                color: '#6b7280',
                cursor: 'pointer'
              }}
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !formData.name.trim()}
              style={{
                padding: '5px 12px',
                background: '#6BA893',
                border: 'none',
                borderRadius: '6px',
                fontSize: '0.8rem',
                fontWeight: '600',
                color: 'white',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                opacity: (isSubmitting || !formData.name.trim()) ? 0.5 : 1
              }}
            >
              {isSubmitting ? '⏳ Erstelle...' : `✓ Erstellen ${items.length > 0 ? '(' + items.length + ')' : ''}`}
            </button>
          </div>
        </form>

        {/* ProductPicker Modal */}
        <ProductPicker
          isOpen={showProductPicker}
          onClose={() => setShowProductPicker(false)}
          onAddItems={handleAddItemsFromPicker}
          existingItems={items}
        />

      </div>
    </div>
  );
};

export default CreateListModal;